import React from "react";
import { Link } from "react-router-dom";
import { ShoppingCart } from "lucide-react";
import { Button } from "../components/ui/button";
import { useCart } from "../App";

const ProductCard = ({ product }) => {
  const { addToCart } = useCart();
  
  const discountedPrice = product.discount_percent > 0 
    ? product.price * (1 - product.discount_percent / 100)
    : product.price;

  const handleAddToCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    await addToCart(product.product_id, 1);
  };

  return (
    <div 
      className="group relative bg-white border border-gray-200 rounded-lg overflow-hidden card-hover"
      data-testid={`product-card-${product.product_id}`}
    >
      {/* Badges */}
      {product.discount_percent > 0 && (
        <span className="product-badge product-badge-sale rounded" data-testid="sale-badge">
          {product.discount_percent}% OFF
        </span>
      )}
      {product.featured && !product.discount_percent && (
        <span className="product-badge product-badge-featured rounded" data-testid="featured-badge">
          Featured
        </span>
      )}

      {/* Stock Indicator */}
      {product.stock !== undefined && (
        <span className={`absolute top-3 right-3 z-10 px-2 py-1 text-xs font-medium rounded ${
          product.stock > 10 ? 'bg-green-100 text-green-700' : 
          product.stock > 0 ? 'bg-yellow-100 text-yellow-700' : 
          'bg-red-100 text-red-700'
        }`}>
          {product.stock > 10 ? 'In Stock' : product.stock > 0 ? `Only ${product.stock} left` : 'Out of Stock'}
        </span>
      )}

      {/* Image */}
      <Link to={`/product/${product.product_id}`} className="block aspect-square overflow-hidden bg-gray-100">
        <img
          src={product.images?.[0] || "https://via.placeholder.com/400x400?text=No+Image"}
          alt={product.name}
          className="w-full h-full object-cover product-image-zoom"
          loading="lazy"
        />
      </Link>

      {/* Content */}
      <div className="p-4">
        <span className="text-xs text-primary uppercase tracking-wider font-medium">
          {product.brand}
        </span>
        <Link to={`/product/${product.product_id}`}>
          <h3 className="text-gray-900 font-medium mt-1 line-clamp-2 hover:text-primary transition-colors" data-testid="product-name">
            {product.name}
          </h3>
        </Link>
        
        {/* Price */}
        <div className="flex items-center gap-2 mt-3">
          {product.discount_percent > 0 && (
            <span className="price-original" data-testid="original-price">${product.price.toFixed(2)}</span>
          )}
          <span className="price-current" data-testid="current-price">${discountedPrice.toFixed(2)}</span>
        </div>

        {/* Add to Cart */}
        <Button
          onClick={handleAddToCart}
          disabled={product.stock === 0}
          className="w-full mt-4 bg-primary hover:bg-primary/90 text-white font-bold uppercase tracking-wider text-sm disabled:opacity-50"
          data-testid="add-to-cart-btn"
        >
          <ShoppingCart className="w-4 h-4 mr-2" />
          {product.stock === 0 ? 'Out of Stock' : 'Add to Cart'}
        </Button>
      </div>
    </div>
  );
};

export default ProductCard;
